import { z } from 'zod'

/**
 * Creates a validator function compatible with react-final-form from a zod schema.
 * Converts zod validation errors to the format expected by react-final-form.
 *
 * @param schema - A zod schema to validate against
 * @returns A validator function that returns an object with field errors (first error per field)
 *
 * @example
 * const schema = z.object({ email: z.string().email() })
 * const validate = createValidator(schema)
 * <Form validate={validate} ... />
 */
export const createValidator =
  <T extends z.ZodSchema>(schema: T) =>
  (data: unknown): Record<string, string> | undefined => {
    const result = schema.safeParse(data)
    if (result.success) {
      return undefined
    }
    const errors: Record<string, string> = {}
    for (const issue of result.error.issues) {
      const field = issue.path[0] as string
      // Only keep the first error per field
      if (!errors[field]) {
        errors[field] = issue.message
      }
    }
    return errors
  }
